import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const { user, cart, logout } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isPublicProfile = location.pathname.startsWith('/p/');
  if (isPublicProfile) return null;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Store', path: '/store' },
    { name: 'Pricing', path: '/pricing' },
  ];

  return (
    <nav className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img src="https://s3.us-east-2.wasabisys.com/crc-cdn/TabBadge/TBL-wide.png" alt="TapBadge" className="h-12 w-auto object-contain" />
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map(link => (
              <Link key={link.name} to={link.path} className="text-gray-600 hover:text-indigo-600 font-medium transition-colors text-lg">
                {link.name}
              </Link>
            ))}
            
            <Link to="/cart" className="relative text-gray-600 hover:text-indigo-600">
              <SafeIcon name="ShoppingCart" className="text-2xl" />
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cart.length}
                </span>
              )}
            </Link>

            {user ? (
              <div className="flex items-center space-x-4">
                <Link to="/dashboard" className="text-gray-600 hover:text-indigo-600 font-medium text-lg">Dashboard</Link>
                <button onClick={logout} className="text-gray-500 hover:text-red-600 p-2">
                  <SafeIcon name="LogOut" className="text-xl" />
                </button>
              </div>
            ) : (
              <Link to="/login" className="bg-gray-900 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-gray-800 transition-colors text-lg">
                Log In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <Link to="/cart" className="relative text-gray-600">
              <SafeIcon name="ShoppingCart" className="text-2xl" />
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-indigo-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center text-[10px]">
                  {cart.length}
                </span>
              )}
            </Link>
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 focus:outline-none">
              <SafeIcon name={isOpen ? "X" : "Menu"} className="text-3xl" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden border-t border-gray-100 bg-white shadow-lg"
          >
            <div className="px-4 pt-2 pb-6 space-y-1">
              {navLinks.map(link => (
                <Link key={link.name} to={link.path} onClick={() => setIsOpen(false)} className="block px-3 py-4 text-lg font-medium text-gray-700 hover:bg-gray-50 rounded-md">
                  {link.name}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-lg font-medium text-gray-700 hover:bg-gray-50 rounded-md">Dashboard</Link>
                  <button onClick={() => { logout(); setIsOpen(false); }} className="w-full text-left px-3 py-4 text-lg font-medium text-red-600 hover:bg-gray-50 rounded-md">
                    Log Out
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-lg font-medium text-indigo-600 hover:bg-gray-50 rounded-md">
                  Log In / Register
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
